import type { ShearAnchorPosition } from '@api/fastAPI.schemas'
import {
  shearExclusionKey,
  SHEAR_PLACEMENTS_AND_POSITIONS,
  type ShearAnchorStoreySelection,
  type ShearPlacement,
} from 'src/components/manager/shearConnections'

export type StoreySelectionChange =
  | { kind: 'save'; selection: ShearAnchorStoreySelection }
  | { kind: 'refused'; message: string }

/**
 * The next selection after one checkbox toggle, or a refusal.
 *
 * Unchecking the last active anchor of a storey's list is refused — the check would
 * silently produce nothing for its walls (the backend rejects it too). Re-checking
 * removes the exclusion again; storeys whose exclusions all disappear drop out of
 * the payload entirely, so the stored state stays minimal.
 */
export const resolveStoreySelectionToggle = ({
  selection,
  storeyKey,
  placement,
  position,
  anchorGuid,
  nextActive,
  orderedGuids,
  listLabel,
}: {
  selection: ShearAnchorStoreySelection
  storeyKey: string
  placement: ShearPlacement
  position: ShearAnchorPosition
  anchorGuid: string
  nextActive: boolean
  /** The storey's applicable prioritization list for this placement and position. */
  orderedGuids: string[]
  /** e.g. "Erdgeschoss Innenwände Wandfuß", for the refusal message. */
  listLabel: string
}): StoreySelectionChange => {
  const key = shearExclusionKey(placement, position)
  const current = selection[storeyKey] ?? {}
  const currentExcluded = current[key] ?? []

  const nextExcluded = nextActive
    ? currentExcluded.filter(guid => guid !== anchorGuid)
    : [...currentExcluded.filter(guid => guid !== anchorGuid), anchorGuid]

  if (!nextActive && orderedGuids.every(guid => nextExcluded.includes(guid))) {
    return {
      kind: 'refused',
      message: `Bitte lassen Sie mindestens einen Schubanker aktiv (${listLabel})`,
    }
  }

  const nextEntry = { ...current, [key]: nextExcluded }
  const next: ShearAnchorStoreySelection = { ...selection, [storeyKey]: nextEntry }
  const isEmpty = SHEAR_PLACEMENTS_AND_POSITIONS.every(
    entry => (nextEntry[shearExclusionKey(entry.placement, entry.position)] ?? []).length === 0,
  )
  if (isEmpty) {
    delete next[storeyKey]
  }
  return { kind: 'save', selection: next }
}
